import { useMemo, useState } from 'react';
import { DollarSign, TrendingUp, CreditCard, Clock, CheckCircle, Calendar } from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer
} from 'recharts';
import { useCommissions, usePayouts } from '@/hooks/useFirestore';
import { GlassCard } from '@/components/ui/GlassCard';
import { Badge } from '@/components/ui/Badge';
import { StatCard } from '@/components/ui/StatCard';
import { formatCurrency, formatDate } from '@/lib/utils';

type TimeRange = '7d' | '30d' | '90d' | 'all';
type ChartView = 'revenue' | 'cumulative' | 'sales';

interface DayData {
  date: string;
  label: string;
  revenue: number;
  cumulative: number;
  sales: number;
}

// Custom tooltip
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  const data = payload[0]?.payload as DayData | undefined;
  if (!data) return null;

  return (
    <div className="bg-[#111]/95 border border-white/10 rounded-lg px-4 py-3 shadow-2xl backdrop-blur-sm">
      <p className="text-gray-400 text-xs mb-2">{label}</p>
      <div className="space-y-1">
        <div className="flex items-center justify-between gap-6">
          <span className="text-gray-500 text-xs">Revenue</span>
          <span className="text-white font-semibold text-sm">{formatCurrency(data.revenue)}</span>
        </div>
        <div className="flex items-center justify-between gap-6">
          <span className="text-gray-500 text-xs">Cumulative</span>
          <span className="text-[#80B0A0] font-semibold text-sm">{formatCurrency(data.cumulative)}</span>
        </div>
        <div className="flex items-center justify-between gap-6">
          <span className="text-gray-500 text-xs">Sales</span>
          <span className="text-[#8B9FD4] font-semibold text-sm">{data.sales}</span>
        </div>
      </div>
    </div>
  );
}

function EarningsChart({
  commissions,
  timeRange,
  onTimeRangeChange,
}: {
  commissions: Array<{ amount?: number; createdAt?: { toDate?: () => Date } }> | undefined;
  timeRange: TimeRange;
  onTimeRangeChange: (range: TimeRange) => void;
}) {
  const [chartView, setChartView] = useState<ChartView>('revenue');

  const timeline: DayData[] = useMemo(() => {
    const now = new Date();
    let startDate: Date;
    switch (timeRange) {
      case '7d': startDate = new Date(now.getTime() - 7 * 86400000); break;
      case '30d': startDate = new Date(now.getTime() - 30 * 86400000); break;
      case '90d': startDate = new Date(now.getTime() - 90 * 86400000); break;
      default: {
        if (!commissions?.length) {
          startDate = new Date(now.getTime() - 30 * 86400000);
        } else {
          startDate = commissions.reduce((min, c) => {
            const d = c.createdAt?.toDate?.() || now;
            return d < min ? d : min;
          }, now);
        }
      }
    }

    // Index by YYYY-MM-DD
    const byDay: Record<string, { revenue: number; sales: number }> = {};
    (commissions || []).forEach(c => {
      const d = c.createdAt?.toDate?.() || new Date();
      if (d < startDate) return;
      const key = d.toISOString().split('T')[0];
      if (!byDay[key]) byDay[key] = { revenue: 0, sales: 0 };
      byDay[key].revenue += (c.amount || 0);
      byDay[key].sales += 1;
    });

    const days: DayData[] = [];
    let cumulative = 0;
    const cursor = new Date(startDate);
    cursor.setHours(0, 0, 0, 0);
    const end = new Date(now);
    end.setHours(23, 59, 59, 999);

    const dayCount = Math.round((end.getTime() - cursor.getTime()) / 86400000);
    const useLongFormat = dayCount <= 14;

    while (cursor <= end) {
      const key = cursor.toISOString().split('T')[0];
      const dayRevenue = byDay[key]?.revenue || 0;
      const daySales = byDay[key]?.sales || 0;
      cumulative += dayRevenue;

      const label = useLongFormat
        ? new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).format(cursor)
        : new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(cursor);

      days.push({
        date: key,
        label,
        revenue: dayRevenue,
        cumulative,
        sales: daySales,
      });
      cursor.setDate(cursor.getDate() + 1);
    }
    return days;
  }, [commissions, timeRange]);

  // Summary KPIs
  const periodRevenue = timeline.reduce((s, d) => s + d.revenue, 0);
  const periodSales = timeline.reduce((s, d) => s + d.sales, 0);
  const avgPerDay = timeline.length > 0 ? periodRevenue / timeline.length : 0;

  // Determine how many X-axis ticks to show
  const tickInterval = timeline.length <= 8 ? 0
    : timeline.length <= 15 ? 1
    : timeline.length <= 35 ? Math.floor(timeline.length / 7) - 1
    : Math.floor(timeline.length / 6) - 1;

  const formatYAxis = (val: number) => {
    if (chartView === 'sales') return String(Math.round(val));
    // Amounts are in cents - convert to dollars for display
    const dollars = val / 100;
    if (dollars >= 10000) return `$${(dollars / 1000).toFixed(0)}k`;
    if (dollars >= 1000) return `$${(dollars / 1000).toFixed(1)}k`;
    if (dollars >= 1) return `$${Math.round(dollars)}`;
    return '$0';
  };

  const dataKey = chartView === 'revenue' ? 'revenue' : chartView === 'cumulative' ? 'cumulative' : 'sales';

  const views: { key: ChartView; label: string; value: string; color: string }[] = [
    { key: 'revenue', label: 'Period Revenue', value: formatCurrency(periodRevenue), color: '#B08080' },
    { key: 'sales', label: 'Sales', value: String(periodSales), color: '#8B9FD4' },
    { key: 'cumulative', label: 'Avg / Day', value: formatCurrency(avgPerDay), color: '#80B0A0' },
  ];

  const activeColor = views.find(v => v.key === chartView)?.color || '#B08080';

  return (
    <div>
      {/* KPI row + time range */}
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div className="flex gap-6">
          {views.map(v => (
            <button key={v.key} onClick={() => setChartView(v.key)} className="text-left group">
              <p className="text-[11px] text-gray-500 mb-0.5 uppercase tracking-wider">{v.label}</p>
              <p className={`text-xl font-bold transition-colors ${
                chartView === v.key ? '' : 'text-gray-500 group-hover:text-gray-300'
              }`} style={chartView === v.key ? { color: v.color } : undefined}>
                {v.value}
              </p>
              {chartView === v.key && (
                <div className="h-0.5 rounded-full mt-1.5" style={{ background: v.color }} />
              )}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <Calendar className="w-3.5 h-3.5 text-gray-500" />
          <div className="flex items-center gap-1 bg-white/5 rounded-lg p-1">
            {(['7d', '30d', '90d', 'all'] as TimeRange[]).map(r => (
              <button
                key={r}
                onClick={() => onTimeRangeChange(r)}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  timeRange === r ? 'bg-[#B08080] text-white' : 'text-gray-400 hover:text-white hover:bg-white/10'
                }`}
              >
                {r === 'all' ? 'All' : r.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Chart */}
      {timeline.length === 0 ? (
        <div className="h-64 flex items-center justify-center text-gray-500 text-sm border border-white/5 rounded-lg">
          No earnings data yet
        </div>
      ) : chartView === 'cumulative' ? (
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={timeline} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <defs>
              <linearGradient id="cumGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={activeColor} stopOpacity={0.3} />
                <stop offset="100%" stopColor={activeColor} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis
              dataKey="label"
              tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 11 }}
              axisLine={{ stroke: 'rgba(255,255,255,0.08)' }}
              tickLine={false}
              interval={tickInterval}
            />
            <YAxis
              tickFormatter={formatYAxis}
              tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              width={60}
            />
            <Tooltip content={<ChartTooltip />} cursor={{ stroke: 'rgba(255,255,255,0.1)' }} />
            <Area
              type="monotone"
              dataKey={dataKey}
              stroke={activeColor}
              strokeWidth={2}
              fill="url(#cumGrad)"
              dot={false}
              activeDot={{ r: 5, fill: '#fff', stroke: activeColor, strokeWidth: 2 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      ) : (
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={timeline} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <defs>
              <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={activeColor} stopOpacity={0.9} />
                <stop offset="100%" stopColor={activeColor} stopOpacity={0.4} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis
              dataKey="label"
              tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 11 }}
              axisLine={{ stroke: 'rgba(255,255,255,0.08)' }}
              tickLine={false}
              interval={tickInterval}
            />
            <YAxis
              tickFormatter={formatYAxis}
              tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              width={60}
              allowDecimals={false}
            />
            <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
            <Bar
              dataKey={dataKey}
              fill="url(#barGrad)"
              radius={[3, 3, 0, 0]}
              maxBarSize={timeline.length < 15 ? 40 : undefined}
            />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

export function EarningsPage() {
  const { data: commissions, isLoading: commissionsLoading } = useCommissions();
  const { data: payouts, isLoading: payoutsLoading } = usePayouts();
  const [timeRange, setTimeRange] = useState<TimeRange>('30d');

  const totalEarnings = commissions?.reduce((acc, c) => acc + (c.amount || 0), 0) || 0;

  const pendingEarnings = commissions?.filter((c) => {
    const status = c.status as string;
    return status === 'pending' || status === 'pending_payout_setup' || status === 'approved';
  }).reduce((acc, c) => acc + (c.amount || 0), 0) || 0;

  const paidEarnings = commissions?.filter((c) => c.status === 'paid').reduce((acc, c) => acc + (c.amount || 0), 0) || 0;

  const isLoading = commissionsLoading || payoutsLoading;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-snow uppercase tracking-widest">Earnings</h1>
        <p className="text-gray-400 mt-1">Track your sales and payouts</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard
          title="Total Earnings"
          value={formatCurrency(totalEarnings)}
          icon={<DollarSign className="w-6 h-6" />}
        />
        <StatCard
          title="Pending"
          value={formatCurrency(pendingEarnings)}
          icon={<Clock className="w-6 h-6" />}
        />
        <StatCard
          title="Paid Out"
          value={formatCurrency(paidEarnings)}
          icon={<CheckCircle className="w-6 h-6" />}
        />
      </div>

      {/* Earnings Trend Chart */}
      <GlassCard>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-primary-500/20 flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-primary-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white">Earnings Over Time</h2>
            <p className="text-sm text-gray-500">Track your sales growth</p>
          </div>
        </div>
        <EarningsChart
          commissions={commissions}
          timeRange={timeRange}
          onTimeRangeChange={setTimeRange}
        />
      </GlassCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <GlassCard>
          <h2 className="text-lg font-semibold text-white mb-6">Recent Sales</h2>

          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 bg-white/10 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : commissions && commissions.length > 0 ? (
            <div className="space-y-4">
              {commissions.slice(0, 5).map((commission) => (
                <div
                  key={commission.id}
                  className="flex items-center justify-between p-4 rounded-lg bg-white/5 border border-white/5"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-500/20 flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-primary-400" />
                    </div>
                    <div>
                      <p className="font-medium text-white">{formatCurrency(commission.amount)}</p>
                      <p className="text-sm text-gray-500">
                        100% to you
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge
                      variant={
                        commission.status === 'paid'
                          ? 'success'
                          : commission.status === 'approved'
                          ? 'default'
                          : 'warning'
                      }
                    >
                      {(commission.status as string) === 'pending_payout_setup' ? 'pending_payout_setup' : commission.status}
                    </Badge>
                    <p className="text-xs text-gray-500 mt-1">
                      {formatDate(commission.createdAt)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="w-12 h-12 rounded-full bg-primary-500/10 flex items-center justify-center mx-auto mb-3">
                <DollarSign className="w-6 h-6 text-primary-400" />
              </div>
              <p className="text-gray-400">No sales yet</p>
              <p className="text-sm text-gray-500">
                Sales come in when businesses buy through your sites
              </p>
            </div>
          )}
        </GlassCard>

        <GlassCard>
          <h2 className="text-lg font-semibold text-white mb-6">Payout History</h2>

          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 bg-white/10 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : payouts && payouts.length > 0 ? (
            <div className="space-y-4">
              {payouts.map((payout) => (
                <div
                  key={payout.id}
                  className="flex items-center justify-between p-4 rounded-lg bg-white/5 border border-white/5"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-500/20 flex items-center justify-center">
                      <CreditCard className="w-5 h-5 text-primary-400" />
                    </div>
                    <div>
                      <p className="font-medium text-white">{formatCurrency(payout.amount)}</p>
                      <p className="text-sm text-gray-500">
                        {payout.commissionIds?.length || 0} {payout.commissionIds?.length === 1 ? 'sale' : 'sales'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge
                      variant={
                        payout.status === 'completed'
                          ? 'success'
                          : payout.status === 'processing'
                          ? 'default'
                          : 'warning'
                      }
                    >
                      {payout.status}
                    </Badge>
                    <p className="text-xs text-gray-500 mt-1">
                      {formatDate(payout.completedAt || payout.createdAt)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="w-12 h-12 rounded-full bg-primary-500/10 flex items-center justify-center mx-auto mb-3">
                <CreditCard className="w-6 h-6 text-primary-400" />
              </div>
              <p className="text-gray-400">No payouts yet</p>
              <p className="text-sm text-gray-500">
                Payouts are processed when you have approved sales
              </p>
            </div>
          )}
        </GlassCard>
      </div>

      <GlassCard>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-accent-500/20 flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-accent-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white">How Earnings Work</h2>
            <p className="text-sm text-gray-500">Understand how sales work</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="p-4 rounded-lg bg-white/5 border border-white/10">
            <h3 className="font-medium text-white mb-2">1. Generate Sites</h3>
            <p className="text-sm text-gray-400">
              Create professional websites for local businesses using our AI-powered tools.
            </p>
          </div>
          <div className="p-4 rounded-lg bg-white/5 border border-white/10">
            <h3 className="font-medium text-white mb-2">2. Close Sales</h3>
            <p className="text-sm text-gray-400">
              When a business buys through your site, the sale goes straight to your Stripe account. You keep 100%.
            </p>
          </div>
          <div className="p-4 rounded-lg bg-white/5 border border-white/10">
            <h3 className="font-medium text-white mb-2">3. Get Paid</h3>
            <p className="text-sm text-gray-400">
              Sales settle into your connected Stripe account. No middleman, no monthly batching.
            </p>
          </div>
        </div>
      </GlassCard>
    </div>
  );
}
